import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  useRef,
  type ReactNode,
} from 'react'
import type { OfflineRuntime } from './runtime'
import type { NetState, OutboxOp } from './types'
import { uuidv7 } from './uuid'
import { envelopeError, isInfrastructureFailure } from './conflicts'

/**
 * React bindings (doc §12): `useOfflineMutation()` is the one hook every
 * feature uses so modules don't reinvent queueing — online it calls the
 * normal endpoint; on network failure or while offline it applies
 * optimistically and queues, per the offline write model (doc §4).
 */

const OfflineContext = createContext<OfflineRuntime | undefined>(undefined)

export function OfflineProvider({ runtime, children }: { runtime: OfflineRuntime; children: ReactNode }) {
  useEffect(() => {
    runtime.start()
    return () => runtime.stop()
  }, [runtime])
  return <OfflineContext.Provider value={runtime}>{children}</OfflineContext.Provider>
}

export function useOfflineRuntime(): OfflineRuntime {
  const runtime = useContext(OfflineContext)
  if (!runtime) throw new Error('useOfflineRuntime must be used inside <OfflineProvider>')
  return runtime
}

/** Optional variant for published components that may mount outside the provider. */
export function useOfflineRuntimeIfAvailable(): OfflineRuntime | undefined {
  return useContext(OfflineContext)
}

export function useNetState(): NetState {
  const runtime = useOfflineRuntime()
  return useSyncExternalStore(
    (onChange) => runtime.subscribe(onChange),
    () => runtime.state,
  )
}

export interface OutboxSummary {
  total: number
  pending: number
  syncing: number
  stuck: number
  ops: OutboxOp[]
}

/** Live outbox view — drives the connection indicator and sync center. */
export function useOutbox(): OutboxSummary {
  const runtime = useOfflineRuntime()
  const [summary, setSummary] = useState<OutboxSummary>({ total: 0, pending: 0, syncing: 0, stuck: 0, ops: [] })
  useEffect(() => {
    let alive = true
    const refresh = async () => {
      const ops = await runtime.outboxOps()
      if (!alive) return
      setSummary({
        total: ops.length,
        pending: ops.filter((op) => op.status === 'pending' || op.status === 'blocked').length,
        syncing: ops.filter((op) => op.status === 'syncing').length,
        stuck: ops.filter((op) => op.status === 'failed' || op.status === 'conflict').length,
        ops,
      })
    }
    void refresh()
    const unsubscribe = runtime.subscribe(() => void refresh())
    return () => {
      alive = false
      unsubscribe()
    }
  }, [runtime])
  return summary
}

/** Queued ops touching one record — the per-record "Pending sync" badge. */
export function useEntityPending(entityId: string | undefined): OutboxOp[] {
  const runtime = useOfflineRuntime()
  const [ops, setOps] = useState<OutboxOp[]>([])
  useEffect(() => {
    if (!entityId) {
      setOps([])
      return undefined
    }
    let alive = true
    const refresh = async () => {
      const rows = await runtime.entityOps(entityId)
      if (alive) setOps(rows)
    }
    void refresh()
    const unsubscribe = runtime.subscribe(() => void refresh())
    return () => {
      alive = false
      unsubscribe()
    }
  }, [runtime, entityId])
  return ops
}

export interface OfflineMutationDef<TInput> {
  module: string
  entityType: string
  method?: OutboxOp['method']
  endpoint: (input: TInput) => string
  payload?: (input: TInput) => unknown
  /** The record the op targets — pre-mint with `uuidv7()` for creates. */
  entityId: (input: TInput) => string
  label: (input: TInput) => string
  /** Stable action code for locale-at-render-time outbox labels. `label`
   * remains the backward-compatible English fallback for older app readers. */
  labelKey?: (input: TInput) => OutboxOp['labelKey']
  labelValues?: (input: TInput) => Record<string, string | number> | undefined
  /** Attachment ids captured offline that must upload first (doc §5/§6). */
  deps?: (input: TInput) => string[]
  /** Mutable versioned edits (§7): the record version the edit was made
   * against — sent as `If-Match` so a moved record classifies as a stale
   * conflict (3-way resolution) instead of silently overwriting. */
  baseVersion?: (input: TInput) => number | undefined
  /** Apply the action to local caches immediately (doc §4 step 2). */
  optimistic?: (input: TInput) => void | Promise<void>
}

export interface OfflineMutationResult<TResult> {
  /** Present when the call reached the server synchronously. */
  result?: TResult | undefined
  /** True when the op was queued for later sync instead. */
  queued: boolean
}

/**
 * The request a call would put on the wire — method, endpoint and body.
 * Two calls with the same key are the same tap; two calls with different
 * keys are different work, however close together they arrive.
 */
export function mutationRequestKey<TInput>(
  def: Pick<OfflineMutationDef<TInput>, 'method' | 'endpoint' | 'payload'>,
  input: TInput,
): string {
  return `${def.method ?? 'POST'} ${def.endpoint(input)} ${JSON.stringify(def.payload ? def.payload(input) : input)}`
}

/**
 * FF-AUDIT-093: one in-flight call per REQUEST. A burst of activations in the
 * same tick (a double tap on a laggy screen) arrives before `isPending` has
 * re-rendered anything; every copy after the first joins the first call's
 * promise instead of minting its own id and key — which is what turned one
 * tap into three numbered records.
 *
 * The key is the request, never the hook instance. One hook instance serves
 * many targets (the work-order runner ticks every step through a single
 * `useToggleStep()`), and an instance-wide guard made ticking step 2 while
 * step 1 was still saving resolve with step 1's answer and never send step 2
 * at all — a silent loss for a technician working at a normal pace on a slow
 * link, where the direct path allows ten seconds per call.
 */
export function coalesceInFlight<T>(
  inFlight: Map<string, Promise<T>>,
  key: string,
  run: () => Promise<T>,
): Promise<T> {
  const joined = inFlight.get(key)
  if (joined) return joined
  const promise = run().finally(() => {
    if (inFlight.get(key) === promise) inFlight.delete(key)
  })
  inFlight.set(key, promise)
  return promise
}

export function useOfflineMutation<TInput, TResult = unknown>(def: OfflineMutationDef<TInput>): {
  mutateAsync: (input: TInput) => Promise<OfflineMutationResult<TResult>>
  isPending: boolean
} {
  const runtime = useOfflineRuntime()
  // A count, not a flag: with distinct requests in flight at once, the first
  // to finish must not report "idle" while the second is still saving.
  const [pendingCount, setPendingCount] = useState(0)
  const isPending = pendingCount > 0

  const enqueue = useCallback(
    async (input: TInput, idempotencyKey: string): Promise<OfflineMutationResult<TResult>> => {
      await def.optimistic?.(input)
      const baseVersion = def.baseVersion?.(input)
      const labelValues = def.labelValues?.(input)
      await runtime.enqueue({
        module: def.module,
        label: def.label(input),
        ...(def.labelKey ? { labelKey: def.labelKey(input) } : {}),
        ...(labelValues ? { labelValues } : {}),
        endpoint: def.endpoint(input),
        method: def.method ?? 'POST',
        payload: def.payload ? def.payload(input) : input,
        idempotencyKey,
        entityType: def.entityType,
        entityId: def.entityId(input),
        ...(baseVersion !== undefined ? { baseVersion } : {}),
        deps: def.deps ? def.deps(input) : [],
      })
      return { queued: true }
    },
    [runtime, def],
  )

  // FF-AUDIT-093 — see `coalesceInFlight` for the rule and its two edges.
  const inFlight = useRef(new Map<string, Promise<OfflineMutationResult<TResult>>>())

  const runMutation = useCallback(
    async (input: TInput): Promise<OfflineMutationResult<TResult>> => {
      setPendingCount((n) => n + 1)
      const idempotencyKey = uuidv7()
      try {
        // Anything captured offline for this record must sync first — the
        // direct path would reference blobs the server has never seen.
        const deps = def.deps ? def.deps(input) : []
        const pendingDeps = (await Promise.all(deps.map((d) => runtime.db.outbox.get(d)))).filter(
          (op) => !!op,
        )
        if (runtime.isOffline || pendingDeps.length > 0) {
          return await enqueue(input, idempotencyKey)
        }

        // Direct path with a short timeout — never hang on a flaky link
        // (doc §8): a network failure falls back to the outbox, and the
        // shared idempotency key makes the maybe-it-landed case safe.
        let res: Response
        try {
          const controller = new AbortController()
          const timeout = setTimeout(() => controller.abort(), 10_000)
          const baseVersion = def.baseVersion?.(input)
          res = await runtime.fetch(def.endpoint(input), {
            method: def.method ?? 'POST',
            headers: {
              'content-type': 'application/json',
              'idempotency-key': idempotencyKey,
              ...(baseVersion !== undefined ? { 'if-match': `"${baseVersion}"` } : {}),
            },
            body: JSON.stringify(def.payload ? def.payload(input) : input),
            signal: controller.signal,
          })
          clearTimeout(timeout)
        } catch {
          runtime.reachability.noteFailure()
          return await enqueue(input, idempotencyKey)
        }
        // Gateway/unavailable answers are the flaky-network case in another
        // costume (dead proxy, cold Worker): writes still succeed locally
        // (doc §8) and the queue retries with backoff. A 5xx that CARRIES a
        // FloorFile error envelope is not that (FF-AUDIT-069): the API ran and
        // failed on this body, so it is thrown to the caller like any other
        // rejection instead of being parked in the outbox and retried forever
        // while the person believes it landed.
        const body: unknown = await res.json().catch(() => undefined)
        if (isInfrastructureFailure(res.status, body)) {
          runtime.reachability.noteFailure()
          return await enqueue(input, idempotencyKey)
        }
        runtime.reachability.noteSuccess()
        // The whole envelope, not just `status` + `code`: a 422's per-field
        // `details` are what lets the form point at the refused answer.
        if (!res.ok) throw envelopeError(res.status, body)
        runtime.onDirectApplied()
        return { result: body as TResult, queued: false }
      } finally {
        setPendingCount((n) => n - 1)
      }
    },
    [runtime, def, enqueue],
  )

  const mutateAsync = useCallback(
    (input: TInput): Promise<OfflineMutationResult<TResult>> =>
      coalesceInFlight(inFlight.current, mutationRequestKey(def, input), () => runMutation(input)),
    [def, runMutation],
  )

  return { mutateAsync, isPending }
}
